import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";

declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
  }
}

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    
    // Set default consent mode (denied) on initial load
    if (window.gtag && !consent) {
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'functionality_storage': 'granted',
        'security_storage': 'granted'
      });
    }

    if (!consent) {
      setShowBanner(true);
    } else if (consent === "analytics-accepted" && window.gtag) {
      // User previously accepted analytics
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("cookie-consent", "analytics-accepted");
    
    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    setShowBanner(false);
  };

  const handleNecessaryOnly = () => {
    localStorage.setItem("cookie-consent", "necessary-only");
    
    // Keep analytics denied
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    if (analyticsEnabled) {
      localStorage.setItem("cookie-consent", "analytics-accepted");
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
    } else {
      localStorage.setItem("cookie-consent", "necessary-only");
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-lg border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="container mx-auto max-w-6xl">
        {!showSettings ? (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">
                Cookie- und Datenschutzeinstellungen
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                Wir verwenden notwendige Cookies für die Funktionalität dieser Website. 
                Zusätzlich nutzen wir <strong>Google Analytics</strong> zur Analyse der Webseitennutzung. 
                Diese Daten helfen uns, die Website zu verbessern.
              </p>
              <p className="text-xs text-muted-foreground">
                Weitere Informationen finden Sie in unserer{" "}
                <a href="/datenschutz" className="text-primary hover:underline">
                  Datenschutzerklärung
                </a>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="whitespace-nowrap"
              >
                <Settings className="h-4 w-4 mr-2" />
                Einstellungen
              </Button>
              <Button
                variant="outline"
                onClick={handleNecessaryOnly}
                className="whitespace-nowrap"
              >
                Nur Notwendige
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="whitespace-nowrap"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-foreground mb-3">
                Cookie-Einstellungen anpassen
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">Notwendige Cookies</h4>
                    <p className="text-xs text-muted-foreground">
                      Erforderlich für die grundlegende Funktionalität der Website. 
                      Können nicht deaktiviert werden.
                    </p>
                  </div>
                  <div className="ml-4 text-sm font-medium text-muted-foreground">
                    Immer aktiv
                  </div>
                </div>

                <div className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">Analyse-Cookies (Google Analytics)</h4>
                    <p className="text-xs text-muted-foreground">
                      Hilft uns zu verstehen, wie Besucher mit der Website interagieren. 
                      Alle Daten werden anonymisiert.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
                className="whitespace-nowrap flex-1 sm:flex-initial"
              >
                Zurück
              </Button>
              <Button
                onClick={handleSaveSettings}
                className="whitespace-nowrap flex-1 sm:flex-initial"
              >
                Einstellungen speichern
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
